import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const hs = window.wpData?.heroSettings ?? {}

const MODE       = hs.mode       || 'slideshow'
const SLIDES     = Array.isArray(hs.slides) && hs.slides.length > 0 ? hs.slides : null
const VIDEO_URL  = hs.video      || ''
const LABEL      = hs.label      || 'Ha Long Bay · UNESCO World Heritage'
const TITLE_MAIN = hs.title_main || 'Sail the Emerald'
const TITLE_EM   = hs.title_em   || 'Waters of Ha Long'
const SUB        = hs.sub        || "Luxury cruises through one of the world's most breathtaking seascapes — refined journeys, elegant spaces, and thoughtfully curated moments on the bay."

// ── SVG placeholder (fallback khi chưa có ảnh) ───────────────────
const PLACEHOLDER_SLIDES = [
  { bg: '#071E30', accent: '#0B3250', label: 'Le Journey Premier' },
  { bg: '#0d3b2e', accent: '#1F6F5F', label: 'Ha Long Bay at Dawn' },
  { bg: '#1a1200', accent: '#4a3000', label: 'Sunset on the Bay' },
]

function SvgPlaceholder({ index }) {
  const s = PLACEHOLDER_SLIDES[index % PLACEHOLDER_SLIDES.length]
  return (
    <svg
      width="100%" height="100%" viewBox="0 0 1440 900"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <rect width="1440" height="900" fill={s.bg} />
      <rect width="1440" height="900" fill={s.accent} opacity="0.4" />
      <text
        x="720" y="450"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontSize="32" fontWeight="300" letterSpacing="8"
        fill="rgba(245,242,236,0.12)"
        textAnchor="middle" dominantBaseline="middle"
      >
        {s.label}
      </text>
    </svg>
  )
}

// ── Video embed (autoplay muted loop, letterbox-fit) ─────────────
function buildEmbedUrl(url) {
  const ytMatch = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}&controls=0&modestbranding=1&playsinline=1`
  const vmMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vmMatch) return `https://player.vimeo.com/video/${vmMatch[1]}?autoplay=1&muted=1&loop=1&background=1`
  const dmMatch = url.match(/dailymotion\.com\/video\/([A-Za-z0-9]+)/)
  if (dmMatch) return `https://www.dailymotion.com/embed/video/${dmMatch[1]}?autoplay=1&mute=1&loop=1&ui-start-screen-info=0`
  return null
}

function VideoHero() {
  const embedUrl = buildEmbedUrl(VIDEO_URL)
  if (!embedUrl) return <SvgPlaceholder index={0} />
  return (
    <iframe
      src={embedUrl}
      style={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: '177.78vh', minWidth: '100%',
        height: '56.25vw', minHeight: '100%',
        border: 'none', pointerEvents: 'none',
      }}
      allow="autoplay; fullscreen"
      allowFullScreen
    />
  )
}

// ── Slideshow ────────────────────────────────────────────────────
function SlideImage({ slide, index, active }) {
  return (
    <AnimatePresence mode="wait">
      {active && (
        <motion.div
          key={slide.url ?? index}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeInOut' }}
          style={{ position: 'absolute', inset: 0 }}
        >
          {slide.url
            ? <img src={slide.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <SvgPlaceholder index={index} />
          }
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const SLIDE_DURATION = 5000

// ── Progress dots ────────────────────────────────────────────────
const progressBarStyle = `
  @keyframes hp-progress {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
`

function ProgressDots({ items, current, onSelect }) {
  return (
    <>
      <style>{progressBarStyle}</style>
      <div className="hp-hero__dots">
        {items.map((_, i) => {
          const isActive = i === current
          const isPast   = i < current
          return (
            <button
              key={i}
              className="hp-hero__dot"
              onClick={() => onSelect(i)}
              aria-label={`Slide ${i + 1}`}
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              {/* filled track */}
              <span style={{
                position: 'absolute', inset: 0,
                background: 'var(--color-gold)',
                transformOrigin: 'left center',
                transform: isPast ? 'scaleX(1)' : 'scaleX(0)',
                ...(isActive ? {
                  animation: `hp-progress ${SLIDE_DURATION}ms linear forwards`,
                } : {}),
              }} />
            </button>
          )
        })}
      </div>
    </>
  )
}

// ── Main ─────────────────────────────────────────────────────────
export default function HeroSection() {
  const items = SLIDES ?? PLACEHOLDER_SLIDES
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (MODE !== 'slideshow') return
    const timer = setTimeout(() => {
      setCurrent(c => (c + 1) % items.length)
    }, SLIDE_DURATION)
    return () => clearTimeout(timer)
  }, [current, items.length])

  const goTo = (i) => setCurrent(i)

  return (
    <section className="hp-hero">

      {/* Background layer */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {MODE === 'video'
          ? <VideoHero />
          : items.map((slide, i) => (
              <SlideImage key={i} slide={slide} index={i} active={i === current} />
            ))
        }
      </div>

      {/* Gradient overlay */}
      <div className="hp-hero__overlay" />

      {/* Progress dots — slideshow only */}
      {MODE === 'slideshow' && (
        <ProgressDots items={items} current={current} onSelect={goTo} />
      )}

      {/* Text — bottom left */}
      <div className="hp-hero__content">
        <motion.div
          className="hp-hero__label"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          {LABEL}
        </motion.div>

        <motion.h1
          className="hp-hero__title"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {TITLE_MAIN && <>{TITLE_MAIN}{TITLE_EM && <br />}</>}
          {TITLE_EM   && <em>{TITLE_EM}</em>}
        </motion.h1>

        {SUB && (
          <motion.p
            className="hp-hero__sub"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.48, ease: [0.22, 1, 0.36, 1] }}
          >
            {SUB}
          </motion.p>
        )}
      </div>

      {/* Scroll indicator */}
      <div className="hp-hero__scroll">
        <div className="hp-hero__scroll-line" />
        <span className="hp-hero__scroll-label">Scroll</span>
      </div>

    </section>
  )
}
